/**
 * Paper Trading State Management & Serialization.
 *
 * Defines the CAS trading state fields and patch filters for adopting server snapshots.
 */

export const TRADING_FIELDS = [
  "startCapital",
  "capital",
  "positions",
  "history",
  "pendingOrders",
  "orderLog",
  "mtmLossLimit",
  "mtmTarget",
  "ceStrikeOffset",
  "peStrikeOffset",
  "defaultQtyLots",
  "dailyLossDate",
  "dailyRealizedLoss",
  "warnedDailyLoss",
  "mtmSquaredOffToday",
  "peakUsedMarginToday",
  "expiryMarginWarnedToday",
  "expiryMarginSquaredOffToday",
] as const;

export type TradingField = (typeof TRADING_FIELDS)[number];

export interface PaperTradingState {
  startCapital: number;
  capital: number;
  positions: any[];
  history: any[];
  pendingOrders: any[];
  orderLog: any[];
  mtmLossLimit: number | null;
  mtmTarget: number | null;
  ceStrikeOffset: number;
  peStrikeOffset: number;
  defaultQtyLots: number;
  dailyLossDate: string;
  dailyRealizedLoss: number;
  warnedDailyLoss: boolean;
  mtmSquaredOffToday: boolean;
  peakUsedMarginToday: number;
  expiryMarginWarnedToday: boolean;
  expiryMarginSquaredOffToday: boolean;
}

/**
 * Builds a clean snapshot containing only the managed trading fields.
 */
export function buildTradingSnapshot(
  storeGetTrading: Record<string, any>,
): Record<string, any> {
  const out: Record<string, any> = {};
  if (!storeGetTrading || typeof storeGetTrading !== "object") return out;
  for (const f of TRADING_FIELDS) {
    if (f in storeGetTrading) {
      out[f] = storeGetTrading[f];
    }
  }
  return out;
}

/**
 * Filters a raw server/saved object into a validated partial patch for the store.
 */
export function filterAdoptTradingPatch(
  saved: any,
): Partial<PaperTradingState> {
  if (!saved || typeof saved !== "object") return {};
  const patch: Partial<PaperTradingState> = {};
  if (typeof saved.startCapital === "number")
    patch.startCapital = saved.startCapital;
  if (typeof saved.capital === "number") patch.capital = saved.capital;
  if (Array.isArray(saved.positions)) patch.positions = saved.positions;
  if (Array.isArray(saved.history)) patch.history = saved.history;
  if (Array.isArray(saved.pendingOrders))
    patch.pendingOrders = saved.pendingOrders;
  if (Array.isArray(saved.orderLog)) patch.orderLog = saved.orderLog;
  if (typeof saved.mtmLossLimit === "number" || saved.mtmLossLimit === null)
    patch.mtmLossLimit = saved.mtmLossLimit;
  if (typeof saved.mtmTarget === "number" || saved.mtmTarget === null)
    patch.mtmTarget = saved.mtmTarget;
  if (typeof saved.ceStrikeOffset === "number")
    patch.ceStrikeOffset = saved.ceStrikeOffset;
  if (typeof saved.peStrikeOffset === "number")
    patch.peStrikeOffset = saved.peStrikeOffset;
  if (typeof saved.defaultQtyLots === "number" && saved.defaultQtyLots > 0)
    patch.defaultQtyLots = saved.defaultQtyLots;
  if (typeof saved.dailyLossDate === "string")
    patch.dailyLossDate = saved.dailyLossDate;
  if (typeof saved.dailyRealizedLoss === "number")
    patch.dailyRealizedLoss = saved.dailyRealizedLoss;
  if (typeof saved.warnedDailyLoss === "boolean")
    patch.warnedDailyLoss = saved.warnedDailyLoss;
  if (typeof saved.mtmSquaredOffToday === "boolean")
    patch.mtmSquaredOffToday = saved.mtmSquaredOffToday;
  if (typeof saved.peakUsedMarginToday === "number")
    patch.peakUsedMarginToday = saved.peakUsedMarginToday;
  if (typeof saved.expiryMarginWarnedToday === "boolean")
    patch.expiryMarginWarnedToday = saved.expiryMarginWarnedToday;
  if (typeof saved.expiryMarginSquaredOffToday === "boolean")
    patch.expiryMarginSquaredOffToday = saved.expiryMarginSquaredOffToday;
  return patch;
}
